// תעודות משלוח — what physically shipped, recorded by hand.
//
// A note is independent of any single order: its lines can be drawn from several
// orders, and one order line can be split across several notes. Nothing is
// inferred — the mode, the quantities and the note number are all typed in.

import { useState, useEffect, useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import * as XLSX from 'xlsx';
import api, { fmtQty } from '../../utils/api';

const MODE_LABEL = {
  he: { AIR: '✈️ אוויר', SEA: '🚢 ים' },
  en: { AIR: '✈️ Air',  SEA: '🚢 Sea' },
};
const MODE_COLOR = { AIR: '#7b1fa2', SEA: '#1565c0' };

const d2 = (n) => String(n).padStart(2, '0');
const fmtDay = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return `${d2(d.getUTCDate())}/${d2(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
};
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${d2(d.getMonth() + 1)}-${d2(d.getDate())}`;
};

export default function DeliveryNotesPage() {
  const { i18n } = useTranslation();
  const { hasRole } = useAuth();
  const he = i18n.language === 'he';
  const canEdit = hasRole('admin', 'warehouse');

  const [notes, setNotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [expandedLines, setExpandedLines] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  // create form
  const [head, setHead] = useState({ noteNumber: '', transportMode: 'SEA', shipDate: todayISO(), arrivalDate: '', destination: '', invoiceNumber: '' });
  const [pickOrder, setPickOrder] = useState('');
  const [openItems, setOpenItems] = useState([]);
  const [picked, setPicked] = useState({});      // itemId -> qty typed
  const [meta, setMeta] = useState({});          // itemId -> {description, orderNumber, remaining}
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);   // note being edited; null = creating a new one

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [n, o] = await Promise.all([
        api.get('/delivery-notes'),
        api.get('/warehouse/cellcom-orders'),
      ]);
      setNotes(n.data.notes || []);
      const list = o.data.orders || o.data || [];
      setOrders(list.filter((x) => x.status !== 'cancelled'));
      setErr('');
    } catch (e) {
      setErr(e.response?.data?.error || (he ? 'שגיאה בטעינה' : 'Failed to load'));
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNote = async (id) => {
    if (expanded === id) { setExpanded(null); setExpandedLines([]); return; }
    try {
      const r = await api.get(`/delivery-notes/${id}`);
      setExpandedLines(r.data.lines || []);
      setExpanded(id);
    } catch { setExpandedLines([]); }
  };

  const loadOpenItems = async (orderNumber) => {
    setPickOrder(orderNumber);
    if (!orderNumber) { setOpenItems([]); return; }
    try {
      const q = editId ? `?exceptNote=${editId}` : '';
      const r = await api.get(`/delivery-notes/order/${orderNumber}/open${q}`);
      const items = (r.data.items || []).filter((i) => parseFloat(i.remaining) > 0);
      setOpenItems(items);
      setMeta((m) => {
        const next = { ...m };
        items.forEach((i) => {
          next[i.item_id] = { description: i.description, orderNumber, remaining: parseFloat(i.remaining) };
        });
        return next;
      });
    } catch { setOpenItems([]); }
  };

  const setQty = (itemId, value) => {
    setPicked((p) => {
      const next = { ...p };
      if (value === '' || value === null) delete next[itemId];
      else next[itemId] = value;
      return next;
    });
  };

  const takeAllOfOrder = () => {
    const next = { ...picked };
    openItems.forEach((i) => { next[i.item_id] = String(parseFloat(i.remaining)); });
    setPicked(next);
  };

  const resetForm = () => {
    setHead({ noteNumber: '', transportMode: 'SEA', shipDate: todayISO(), arrivalDate: '', destination: '', invoiceNumber: '' });
    setPicked({}); setMeta({}); setOpenItems([]); setPickOrder(''); setEditId(null);
  };

  // Load an existing note into the form. Its own lines are pre-filled, and the
  // remainder shown for every item excludes this note (see ?exceptNote on the API),
  // so quantities it already holds can be reduced as well as increased.
  const startEdit = async (note) => {
    try {
      const r = await api.get(`/delivery-notes/${note.id}`);
      const lines = r.data.lines || [];
      setEditId(note.id);
      setHead({
        noteNumber: note.note_number,
        transportMode: note.transport_mode,
        shipDate: note.ship_date ? String(note.ship_date).slice(0, 10) : '',
        arrivalDate: note.arrival_date ? String(note.arrival_date).slice(0, 10) : '',
        destination: note.destination || '',
        invoiceNumber: note.invoice_number || '',
      });
      const p = {}, m = {};
      lines.forEach((l) => {
        p[l.item_id] = String(parseFloat(l.quantity));
        m[l.item_id] = { description: l.description, orderNumber: l.order_number, remaining: null };
      });
      setPicked(p); setMeta(m); setOpenItems([]); setPickOrder('');
      setShowCreate(true); setExpanded(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setErr(e.response?.data?.error || (he ? 'שגיאה בטעינת התעודה לעריכה' : 'Failed to load the note for editing'));
    }
  };

  const pickedList = Object.entries(picked)
    .map(([id, q]) => ({ itemId: Number(id), quantity: parseFloat(q), ...(meta[id] || {}) }))
    .filter((l) => l.quantity > 0);
  const pickedUnits = pickedList.reduce((s, l) => s + l.quantity, 0);
  const pickedOrders = [...new Set(pickedList.map((l) => l.orderNumber).filter(Boolean))];
  const overPicked = pickedList.filter((l) => l.remaining != null && l.quantity > l.remaining);

  const save = async () => {
    setSaving(true); setErr('');
    const lines = pickedList.map((l) => ({ itemId: l.itemId, quantity: l.quantity }));
    try {
      if (editId) await api.put(`/delivery-notes/${editId}`, { ...head, lines });
      else await api.post('/delivery-notes', { ...head, lines });
      resetForm(); setShowCreate(false); await load();
    } catch (e) {
      setErr(e.response?.data?.error || (he ? 'שגיאה בשמירה' : 'Failed to save'));
    }
    setSaving(false);
  };

  // Export one note to XLSX at the END-CUSTOMER price (cellcom_order_items.selling_price),
  // which is what the final invoice is raised on. Quantity is the note's quantity, not the
  // order's, so a partially-shipped line prices only what actually went out.
  const exportNote = async (note) => {
    try {
      const r = await api.get(`/delivery-notes/${note.id}`);
      const lines = r.data.lines || [];
      const rows = lines.map((l) => {
        const qty = parseFloat(l.quantity) || 0;
        const price = parseFloat(l.selling_price) || 0;
        return he ? {
          'ברקוד': l.barcode || '',
          'מק"ט': l.sku,
          'מוצר': l.description,
          'הזמנה': l.order_number,
          'כמות': qty,
          'מחיר סופי': price,
          'סה"כ': Math.round(qty * price * 100) / 100,
        } : {
          'Barcode': l.barcode || '',
          'SKU': l.sku,
          'Product': l.description,
          'Order': l.order_number,
          'Qty': qty,
          'Final price': price,
          'Total': Math.round(qty * price * 100) / 100,
        };
      });
      const totalQty = rows.reduce((a, x) => a + (x[he ? 'כמות' : 'Qty'] || 0), 0);
      const totalVal = Math.round(rows.reduce((a, x) => a + (x[he ? 'סה"כ' : 'Total'] || 0), 0) * 100) / 100;
      if (rows.length) {
        const blank = {}; Object.keys(rows[0]).forEach((k) => { blank[k] = ''; });
        rows.push({ ...blank });
        rows.push({ ...blank, [he ? 'מוצר' : 'Product']: he ? 'סה"כ' : 'TOTAL',
                    [he ? 'כמות' : 'Qty']: totalQty, [he ? 'סה"כ' : 'Total']: totalVal });
      }
      const modeTxt = MODE_LABEL[he ? 'he' : 'en'][note.transport_mode] || note.transport_mode;
      const header = [
        [he ? 'תעודת משלוח' : 'Delivery note', note.note_number],
        [he ? 'הובלה' : 'Mode', modeTxt],
        [he ? 'תאריך שילוח' : 'Ship date', fmtDay(note.ship_date)],
        [he ? 'הגעה משוערת' : 'Est. arrival', fmtDay(note.arrival_date)],
        [he ? 'יעד' : 'Destination', note.destination || ''],
        [he ? 'חשבונית' : 'Invoice', note.invoice_number || ''],
        [],
      ];
      const ws = XLSX.utils.aoa_to_sheet(header);
      XLSX.utils.sheet_add_json(ws, rows, { origin: -1 });
      ws['!cols'] = [{ wch: 16 }, { wch: 12 }, { wch: 34 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 14 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `DN ${note.note_number}`);
      XLSX.writeFile(wb, `delivery_note_${note.note_number}.xlsx`);
    } catch (e) {
      setErr(e.response?.data?.error || (he ? 'שגיאה בייצוא' : 'Export failed'));
    }
  };

  const removeNote = async (id, number) => {
    const msg = he
      ? `למחוק את תעודה ${number}? הכמויות יחזרו להיות פנויות לשיבוץ.`
      : `Delete note ${number}? Its quantities become available to assign again.`;
    if (!window.confirm(msg)) return;
    try { await api.delete(`/delivery-notes/${id}`); await load(); }
    catch (e) { setErr(e.response?.data?.error || (he ? 'שגיאה במחיקה' : 'Failed to delete')); }
  };

  return (
    <div className="page" dir={he ? 'rtl' : 'ltr'}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>📦 {he ? 'תעודות משלוח' : 'Delivery Notes'}</h2>
        <span style={{ color: '#64748b', fontSize: 13 }}>
          {notes.length} {he ? "תעודות · מה שיצא בפועל, ים או אוויר" : 'notes · what actually shipped, sea or air'}
        </span>
        {canEdit && (
          <button className="btn btn-sm btn-gold" style={{ marginInlineStart: 'auto' }}
            onClick={() => { setShowCreate((v) => !v); if (!showCreate) resetForm(); else resetForm(); }}>
            {showCreate ? (he ? '✕ סגור' : '✕ Close') : (he ? '➕ תעודה חדשה' : '➕ New note')}
          </button>
        )}
      </div>

      {err && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
          padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{err}</div>
      )}

      {showCreate && canEdit && (
        <div style={{ background: editId ? '#f0fdfa' : '#f8fafc', border: `1px solid ${editId ? '#99f6e4' : '#e2e8f0'}`, borderRadius: 8, padding: 16, marginBottom: 20 }}>
          {editId && (
            <div style={{ marginBottom: 10, fontSize: 13, fontWeight: 700, color: '#0f766e' }}>
              ✏️ {he ? `עריכת תעודה ${head.noteNumber} — אפשר לשנות כמויות ולהוסיף שורות מהזמנות נוספות`
                    : `Editing note ${head.noteNumber} — change quantities and add lines from other orders`}
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            <label style={{ fontSize: 12 }}>{he ? 'מספר תעודה' : 'Note no.'} *<br />
              <input className="inline-edit" style={{ width: 140 }} value={head.noteNumber} disabled={!!editId}
                onChange={(e) => setHead({ ...head, noteNumber: e.target.value })} placeholder="13538686" />
            </label>
            <label style={{ fontSize: 12 }}>{he ? 'הובלה' : 'Mode'} *<br />
              <select className="inline-edit" style={{ width: 110 }} value={head.transportMode}
                onChange={(e) => setHead({ ...head, transportMode: e.target.value })}>
                <option value="SEA">{he ? '🚢 ים' : '🚢 Sea'}</option>
                <option value="AIR">{he ? '✈️ אוויר' : '✈️ Air'}</option>
              </select>
            </label>
            <label style={{ fontSize: 12 }}>{he ? 'תאריך שילוח' : 'Ship date'}<br />
              <input className="inline-edit" type="date" style={{ width: 145 }} value={head.shipDate}
                onChange={(e) => setHead({ ...head, shipDate: e.target.value })} />
            </label>
            <label style={{ fontSize: 12 }}>{he ? 'הגעה משוערת' : 'Est. arrival'}<br />
              <input className="inline-edit" type="date" style={{ width: 145 }} value={head.arrivalDate}
                onChange={(e) => setHead({ ...head, arrivalDate: e.target.value })} />
            </label>
            <label style={{ fontSize: 12 }}>{he ? 'יעד' : 'Destination'}<br />
              <input className="inline-edit" style={{ width: 170 }} value={head.destination}
                onChange={(e) => setHead({ ...head, destination: e.target.value })} placeholder={he ? 'סלקום' : 'Cellcom'} />
            </label>
            <label style={{ fontSize: 12 }}>{he ? 'חשבונית' : 'Invoice'}<br />
              <input className="inline-edit" style={{ width: 110 }} value={head.invoiceNumber}
                onChange={(e) => setHead({ ...head, invoiceNumber: e.target.value })} placeholder="1074" />
            </label>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 10, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12 }}>{he ? 'הוסף שורות מהזמנה' : 'Add lines from order'}<br />
              <select className="inline-edit" style={{ width: 190 }} value={pickOrder}
                onChange={(e) => loadOpenItems(e.target.value)}>
                <option value="">{he ? '— בחר הזמנה —' : '— pick an order —'}</option>
                {orders.map((o) => (
                  <option key={o.order_number} value={o.order_number}>
                    {o.order_number} · {o.status}
                  </option>
                ))}
              </select>
            </label>
            {pickOrder && openItems.length > 0 && (
              <button className="btn btn-sm btn-outline" onClick={takeAllOfOrder}>
                ⬇️ {he ? `כל היתרה של ${pickOrder}` : `All remaining of ${pickOrder}`}
              </button>
            )}
            <span style={{ fontSize: 12, color: '#64748b' }}>
              {he ? 'אפשר לבחור כמה הזמנות — הן מצטברות לאותה תעודה' : 'Pick several orders — lines accumulate onto the same note'}
            </span>
          </div>

          {pickedList.length > 0 && (
            <div className="table-wrap" style={{ maxHeight: 240, overflow: 'auto', marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0f766e', margin: '2px 0 6px' }}>
                {he ? 'שורות בתעודה' : 'Lines on this note'}
              </div>
              <table className="table" style={{ fontSize: 12 }}>
                <thead><tr>
                  <th>{he ? 'הזמנה' : 'Order'}</th><th>{he ? 'מוצר' : 'Product'}</th><th>{he ? 'ברקוד' : 'Barcode'}</th>
                  <th className="text-end">{he ? 'כמות' : 'Qty'}</th><th style={{ width: 40 }}></th>
                </tr></thead>
                <tbody>
                  {pickedList.map((l) => (
                    <tr key={l.itemId}>
                      <td style={{ fontFamily: "'DM Mono', monospace" }}>{l.orderNumber || '—'}</td>
                      <td>{l.description || `#${l.itemId}`}</td>
                      <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 11 }}>{l.barcode || '—'}</td>
                      <td className="text-end">
                        <input className="inline-edit" type="number" min="0" step="1"
                          style={{ width: 90, textAlign: 'right',
                                   borderColor: (l.remaining != null && l.quantity > l.remaining) ? '#dc2626' : undefined }}
                          value={picked[l.itemId] ?? ''} onChange={(e) => setQty(l.itemId, e.target.value)} />
                      </td>
                      <td>
                        <button className="btn btn-sm btn-outline" style={{ color: '#b91c1c', borderColor: '#fecaca' }}
                          title={he ? 'הסר מהתעודה' : 'Remove from note'} onClick={() => setQty(l.itemId, '')}>✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pickOrder && (
            <div className="table-wrap" style={{ maxHeight: 280, overflow: 'auto', marginBottom: 12 }}>
              <table className="table" style={{ fontSize: 12 }}>
                <thead><tr>
                  <th>{he ? 'מוצר' : 'Product'}</th><th>{he ? 'מק"ט' : 'SKU'}</th><th>{he ? 'ברקוד' : 'Barcode'}</th>
                  <th className="text-end">{he ? 'בהזמנה' : 'Ordered'}</th>
                  <th className="text-end">{he ? 'כבר שובץ' : 'Assigned'}</th>
                  <th className="text-end">{he ? 'נותר' : 'Remaining'}</th>
                  <th className="text-end">{he ? 'לתעודה זו' : 'On this note'}</th>
                </tr></thead>
                <tbody>
                  {openItems.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: '#64748b', padding: 14 }}>
                      {he ? 'כל ההזמנה כבר משובצת לתעודות' : 'The whole order is already assigned to notes'}
                    </td></tr>
                  )}
                  {openItems.map((i) => {
                    const val = picked[i.item_id] ?? '';
                    const over = val !== '' && parseFloat(val) > parseFloat(i.remaining);
                    return (
                      <tr key={i.item_id}>
                        <td>{i.description}</td>
                        <td style={{ fontFamily: "'DM Mono', monospace" }}>{i.sku}</td>
                        <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 11 }}>{i.barcode || '—'}</td>
                        <td className="text-end">{fmtQty(i.order_qty)}</td>
                        <td className="text-end" style={{ color: '#64748b' }}>{fmtQty(i.assigned)}</td>
                        <td className="text-end" style={{ fontWeight: 600 }}>{fmtQty(i.remaining)}</td>
                        <td className="text-end">
                          <input className="inline-edit" type="number" min="0" step="1"
                            style={{ width: 90, textAlign: 'right', borderColor: over ? '#dc2626' : undefined }}
                            value={val} onChange={(e) => setQty(i.item_id, e.target.value)} placeholder="0" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              {he ? `נבחרו ${pickedList.length} שורות · ${fmtQty(pickedUnits)} יח'` : `${pickedList.length} lines · ${fmtQty(pickedUnits)} units`}
              {pickedOrders.length > 1 && (he ? ` · מ-${pickedOrders.length} הזמנות (${pickedOrders.join(', ')})` : ` · from ${pickedOrders.length} orders (${pickedOrders.join(', ')})`)}
            </span>
            {overPicked.length > 0 && (
              <span style={{ color: '#b91c1c', fontSize: 12 }}>
                ⚠ {overPicked.length} {he ? 'שורות חורגות מהיתרה' : 'lines exceed the remainder'}
              </span>
            )}
            <button className="btn btn-sm btn-gold" style={{ marginInlineStart: 'auto' }}
              disabled={saving || !head.noteNumber.trim() || !pickedList.length || overPicked.length > 0}
              onClick={save}>
              {saving ? (he ? 'שומר…' : 'Saving…') : (editId ? (he ? '💾 שמור שינויים' : '💾 Save changes') : (he ? '💾 צור תעודה' : '💾 Create note'))}
            </button>
          </div>
        </div>
      )}

      {loading ? <div style={{ padding: 20 }}>{he ? 'טוען…' : 'Loading…'}</div> : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr>
              <th>{he ? 'תעודה' : 'Note'}</th><th>{he ? 'הובלה' : 'Mode'}</th><th>{he ? 'שילוח' : 'Shipped'}</th><th>{he ? 'הגעה' : 'Arrival'}</th><th>{he ? 'הזמנות' : 'Orders'}</th>
              <th className="text-end">{he ? 'יחידות' : 'Units'}</th><th className="text-end">{he ? 'שורות' : 'Lines'}</th>
              <th>{he ? 'יעד' : 'Destination'}</th><th>{he ? 'חשבונית' : 'Invoice'}</th><th></th>
            </tr></thead>
            <tbody>
              {notes.length === 0 && (
                <tr><td colSpan={10} style={{ textAlign: 'center', color: '#64748b', padding: 24 }}>
                  {he ? 'אין עדיין תעודות משלוח' : 'No delivery notes yet'}
                </td></tr>
              )}
              {notes.map((n) => (
                <Fragment key={n.id}>
                  <tr style={{ cursor: 'pointer' }} onClick={() => openNote(n.id)}>
                    <td style={{ fontFamily: "'DM Mono', monospace", fontWeight: 700 }}>
                      {expanded === n.id ? '▾ ' : '▸ '}{n.note_number}
                    </td>
                    <td style={{ color: MODE_COLOR[n.transport_mode], fontWeight: 600 }}>
                      {MODE_LABEL[he ? 'he' : 'en'][n.transport_mode] || n.transport_mode}
                    </td>
                    <td>{fmtDay(n.ship_date)}</td>
                    <td>{fmtDay(n.arrival_date)}</td>
                    <td style={{ fontFamily: "'DM Mono', monospace" }}>{n.orders || '—'}</td>
                    <td className="text-end" style={{ fontWeight: 600 }}>{fmtQty(n.total_units)}</td>
                    <td className="text-end" style={{ color: '#64748b' }}>{n.line_count}</td>
                    <td>{n.destination || '—'}</td>
                    <td style={{ fontFamily: "'DM Mono', monospace" }}>{n.invoice_number || '—'}</td>
                    <td>
                      {canEdit && (
                        <>
                          <button className="btn btn-sm btn-outline" style={{ color: '#15803d', borderColor: '#bbf7d0', marginInlineEnd: 6 }}
                            onClick={(e) => { e.stopPropagation(); exportNote(n); }}>{he ? '📥 אקסל' : '📥 Excel'}</button>
                          <button className="btn btn-sm btn-outline" style={{ color: '#0f766e', borderColor: '#99f6e4', marginInlineEnd: 6 }}
                            onClick={(e) => { e.stopPropagation(); startEdit(n); }}>{he ? 'ערוך / הוסף שורות' : 'Edit / add lines'}</button>
                          <button className="btn btn-sm btn-outline" style={{ color: '#b91c1c', borderColor: '#fecaca' }}
                            onClick={(e) => { e.stopPropagation(); removeNote(n.id, n.note_number); }}>{he ? 'מחק' : 'Delete'}</button>
                        </>
                      )}
                    </td>
                  </tr>
                  {expanded === n.id && (
                    <tr>
                      <td colSpan={10} style={{ background: '#f8fafc', padding: 0 }}>
                        <table className="table" style={{ fontSize: 12, margin: 0 }}>
                          <thead><tr>
                            <th>{he ? 'הזמנה' : 'Order'}</th><th>{he ? 'מוצר' : 'Product'}</th><th>{he ? 'מק"ט' : 'SKU'}</th><th>{he ? 'ברקוד' : 'Barcode'}</th>
                            <th className="text-end">{he ? 'בתעודה' : 'On note'}</th><th className="text-end">{he ? 'בהזמנה' : 'Ordered'}</th>
                          </tr></thead>
                          <tbody>
                            {expandedLines.map((l) => (
                              <tr key={l.id}>
                                <td style={{ fontFamily: "'DM Mono', monospace" }}>{l.order_number}</td>
                                <td>{l.description}</td>
                                <td style={{ fontFamily: "'DM Mono', monospace" }}>{l.sku}</td>
                                <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 11 }}>{l.barcode || '—'}</td>
                                <td className="text-end" style={{ fontWeight: 600 }}>{fmtQty(l.quantity)}</td>
                                <td className="text-end" style={{ color: '#64748b' }}>{fmtQty(l.order_qty)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
